import { Controller } from "@hotwired/stimulus"

// Mounts the Pagefind search UI into this element. The Pagefind bundle is only
// emitted by a production build (`rake deploy` → output/pagefind/), so on the dev
// server this degrades gracefully to a short notice. The UI's CSS + JS are
// injected here rather than via inline <script>/<link> in the page so everything
// stays same-origin and within the site's strict CSP. Reconnects cleanly across
// Turbo visits.
//
// Paths come in as values (data-search-js-value / data-search-css-value) so the
// ERB can run them through `relative_url` and honor any base_path.
export default class extends Controller {
  static values = { js: String, css: String }

  connect() {
    this.#injectCss()
    this.#loadJs()
      .then(() => this.#mount())
      .catch(() => this.#renderUnavailable())
  }

  disconnect() {
    this.ui?.destroy?.()
    this.ui = null
    this.element.replaceChildren()
  }

  #mount() {
    if (!window.PagefindUI) return this.#renderUnavailable()
    this.ui = new window.PagefindUI({
      element: this.element,
      showSubResults: true,
      showImages: false,
    })
  }

  #renderUnavailable() {
    this.element.innerHTML =
      '<p class="search-unavailable">Search isn’t available in this build. ' +
      "Run <code>bundle exec rake deploy</code> to generate the index.</p>"
  }

  #injectCss() {
    if (!this.cssValue) return
    if (document.querySelector(`link[href="${this.cssValue}"]`)) return
    const link = document.createElement("link")
    link.rel = "stylesheet"
    link.href = this.cssValue
    document.head.append(link)
  }

  // Load the Pagefind UI script once per page load, sharing the in-flight promise
  // across controller instances / Turbo re-renders.
  #loadJs() {
    if (window.PagefindUI) return Promise.resolve()
    if (window.__pagefindUILoading) return window.__pagefindUILoading

    window.__pagefindUILoading = new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.src = this.jsValue
      script.onload = resolve
      script.onerror = () => reject(new Error("pagefind-ui failed to load"))
      document.head.append(script)
    })
    return window.__pagefindUILoading
  }
}
